import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import CatalogAPI from 'api/faber/CatalogAPI'
import OrbitSession from 'hooks/useCookies'

interface EditCatalogDialogProps {
  catalogId: string
}

type CatalogDetails = Record<string, unknown>
type PriceItem = {
  value?: string
  list_amount?: string
  symbol?: string
}

const EDITABLE_FIELDS = [
  'vendor',
  'packageType',
  'packageTypeId',
  'name',
  'description',
  'callout',
  'dataInGB',
  'voiceInMins',
  'sms',
  'validityInDays',
  'countries',
  'visibility',
  'supports5g',
  'redirectUrl',
  'tags',
  'prices',
  'esimConfigId',
  'catalogType',
  'catalogMetadata'
]

const READ_ONLY_FIELDS = [
  'id',
  'catalogId',
  'pageId',
  'createdAt',
  'updatedAt',
  'deletedAt'
]

const FIELD_ORDER = [
  'catalogId',
  'createdAt',
  'updatedAt',
  'deletedAt',
  'vendor',
  'packageType',
  'packageTypeId',
  'name',
  'description',
  'callout',
  'dataInGB',
  'voiceInMins',
  'sms',
  'validityInDays',
  'countries',
  'visibility',
  'supports5g',
  'redirectUrl',
  'tags',
  'esimConfigId',
  'catalogType',
  'catalogMetadata',
  'id',
  'pageId',
  'prices'
]

const BOOL_FIELDS = new Set(['visibility', 'supports5g'])
const JSON_LIKE_FIELDS = new Set(['description', 'countries', 'tags', 'prices', 'catalogMetadata'])

const toLabel = (key: string) =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isPriceItem = (value: unknown): value is PriceItem =>
  isObject(value)

const normalizeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalizeValue)
  }

  if (isObject(value)) {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalizeValue(value[key])
        return acc
      }, {})
  }

  return value
}

const isSameValue = (left: unknown, right: unknown) =>
  JSON.stringify(normalizeValue(left)) === JSON.stringify(normalizeValue(right))

const parseInputValue = (field: string, rawValue: string, currentValue: unknown): unknown => {
  if (JSON_LIKE_FIELDS.has(field)) {
    try {
      return rawValue.trim() ? JSON.parse(rawValue) : field === 'prices' ? {} : []
    } catch {
      return rawValue
    }
  }

  if (typeof currentValue === 'number') {
    const parsed = Number(rawValue)
    return Number.isNaN(parsed) ? rawValue : parsed
  }

  return rawValue
}

const toTagArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || '').trim())
      .filter(Boolean)
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }

  return []
}

export default function EditCatalogDialog({ catalogId }: EditCatalogDialogProps) {
  const faberToken = OrbitSession.get('faberToken')
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isPricesExpanded, setIsPricesExpanded] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const [countryInput, setCountryInput] = useState('')
  const [formData, setFormData] = useState<CatalogDetails>({})
  const [initialData, setInitialData] = useState<CatalogDetails>({})

  const changedPayload = useMemo(() => {
    return EDITABLE_FIELDS.reduce<Record<string, unknown>>((acc, field) => {
      if (!(field in formData)) {
        return acc
      }

      const currentValue = formData[field]
      const initialValue = initialData[field]

      if (!isSameValue(currentValue, initialValue)) {
        acc[field] = currentValue
      }

      return acc
    }, {})
  }, [formData, initialData])

  const hasChanges = Object.keys(changedPayload).length > 0

  useEffect(() => {
    const fetchCatalogDetails = async () => {
      if (!open || !catalogId || !faberToken) {
        return
      }

      setIsLoading(true)
      try {
        const response = await CatalogAPI.getCatalogDetails(faberToken, catalogId)
        const root = (response || {}) as {
          data?: unknown
          body?: unknown
        }
        const payload = (root.data || root.body || root) as unknown
        let resolvedData: CatalogDetails = {}

        if (Array.isArray(payload)) {
          resolvedData = ((payload[0] as CatalogDetails) || {}) as CatalogDetails
        } else if (
          payload &&
          typeof payload === 'object' &&
          Array.isArray((payload as { data?: unknown }).data)
        ) {
          resolvedData = ((((payload as { data?: unknown }).data as unknown[])[0] as CatalogDetails) || {}) as CatalogDetails
        } else {
          resolvedData = ((payload as CatalogDetails) || {}) as CatalogDetails
        }

        setFormData(resolvedData)
        setInitialData(resolvedData)
      } finally {
        setIsLoading(false)
      }
    }

    void fetchCatalogDetails()
  }, [catalogId, faberToken, open])

  const fieldsToRender = useMemo(() => {
    const seen = new Set<string>()
    const ordered = FIELD_ORDER.filter((key) => {
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })

    Object.keys(formData).forEach((key) => {
      if (!seen.has(key)) {
        ordered.push(key)
      }
    })

    const pricesIndex = ordered.indexOf('prices')
    if (pricesIndex !== -1) {
      ordered.splice(pricesIndex, 1)
      ordered.push('prices')
    }

    return ordered
  }, [formData])

  const handleChangeField = (field: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handlePriceChange = (currency: string, key: keyof PriceItem, nextValue: string) => {
    setFormData((prev) => {
      const currentPrices = isObject(prev.prices) ? prev.prices : {}
      const currentCurrencyPrice = isPriceItem(currentPrices[currency])
        ? (currentPrices[currency] as PriceItem)
        : {}

      return {
        ...prev,
        prices: {
          ...currentPrices,
          [currency]: {
            ...currentCurrencyPrice,
            [key]: nextValue
          }
        }
      }
    })
  }

  const handleAddTag = () => {
    const nextTag = tagInput.trim()
    if (!nextTag) {
      return
    }

    const existingTags = toTagArray(formData.tags)
    if (existingTags.includes(nextTag)) {
      setTagInput('')
      return
    }

    handleChangeField('tags', [...existingTags, nextTag])
    setTagInput('')
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const existingTags = toTagArray(formData.tags)
    handleChangeField(
      'tags',
      existingTags.filter((tag) => tag !== tagToRemove)
    )
  }

  const handleAddCountry = () => {
    const nextCountry = countryInput.trim().toUpperCase()
    if (!nextCountry) {
      return
    }

    const existingCountries = toTagArray(formData.countries)
    if (existingCountries.includes(nextCountry)) {
      setCountryInput('')
      return
    }

    handleChangeField('countries', [...existingCountries, nextCountry])
    setCountryInput('')
  }

  const handleRemoveCountry = (countryToRemove: string) => {
    const existingCountries = toTagArray(formData.countries)
    handleChangeField(
      'countries',
      existingCountries.filter((country) => country !== countryToRemove)
    )
  }

  const handleSubmit = async () => {
    if (!hasChanges) {
      toast.info('No changes found.')
      return
    }

    if (!faberToken || !catalogId) {
      toast.error('Unable to update catalog right now.')
      return
    }

    setIsSubmitting(true)
    try {
      console.log('Catalog update payload:', changedPayload)
      await CatalogAPI.updateCatalogDetails(faberToken, catalogId, changedPayload)
      setInitialData(formData)
      toast.success('Catalog updated successfully.')
      setOpen(false)
    } catch (error) {
      toast.error('Failed to update catalog.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderField = (field: string) => {
    const value = formData[field]
    const isReadOnly = READ_ONLY_FIELDS.includes(field)
    const isEditable = EDITABLE_FIELDS.includes(field)
    const disabled = isReadOnly || !isEditable

    if (BOOL_FIELDS.has(field)) {
      return (
        <FormControlLabel
          key={field}
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(event) => handleChangeField(field, event.target.checked)}
              disabled={disabled}
            />
          }
          label={toLabel(field)}
        />
      )
    }

    if (field === 'prices') {
      const prices = isObject(value) ? value : {}
      const currencies = Object.keys(prices).sort()

      return (
        <Accordion
          key={field}
          expanded={isPricesExpanded}
          onChange={(_, expanded) => setIsPricesExpanded(expanded)}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            backgroundColor: 'grey.50'
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
              {toLabel(field)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {currencies.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                No pricing data available.
              </Typography>
            ) : (
              <Box display='flex' flexDirection='column' gap={1.5}>
                {currencies.map((currency) => {
                  const priceItem = isPriceItem(prices[currency]) ? (prices[currency] as PriceItem) : {}
                  return (
                    <Box
                      key={currency}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        backgroundColor: 'common.white'
                      }}
                    >
                      <Typography variant='body2' sx={{ mb: 1, fontWeight: 600 }}>
                        {currency.toUpperCase()}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size='small'
                            label='Value'
                            value={priceItem.value || ''}
                            disabled={disabled}
                            onChange={(event) => handlePriceChange(currency, 'value', event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size='small'
                            label='List Amount'
                            value={priceItem.list_amount || ''}
                            disabled={disabled}
                            onChange={(event) =>
                              handlePriceChange(currency, 'list_amount', event.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size='small'
                            label='Symbol'
                            value={priceItem.symbol || ''}
                            disabled={disabled}
                            onChange={(event) => handlePriceChange(currency, 'symbol', event.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )
                })}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )
    }

    if (field === 'tags') {
      const tags = toTagArray(value)

      return (
        <Box
          key={field}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 2,
            backgroundColor: 'grey.50'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1.5 }}>
            {toLabel(field)}
          </Typography>
          <Box display='flex' gap={1} mb={1.5}>
            <TextField
              fullWidth
              size='small'
              label='Add Tag'
              value={tagInput}
              disabled={disabled}
              onChange={(event) => setTagInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleAddTag()
                }
              }}
            />
            <Button
              variant='contained'
              onClick={handleAddTag}
              disabled={disabled || !tagInput.trim()}
            >
              Add
            </Button>
          </Box>
          <Box display='flex' flexWrap='wrap' gap={1}>
            {tags.length > 0 ? (
              tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  color='primary'
                  variant='outlined'
                  onDelete={disabled ? undefined : () => handleRemoveTag(tag)}
                />
              ))
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No tags added.
              </Typography>
            )}
          </Box>
        </Box>
      )
    }

    if (field === 'countries') {
      const countries = toTagArray(value)

      return (
        <Box
          key={field}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 2,
            backgroundColor: 'grey.50'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1.5 }}>
            {toLabel(field)}
          </Typography>
          <Box display='flex' gap={1} mb={1.5}>
            <TextField
              fullWidth
              size='small'
              label='Add Country Code'
              value={countryInput}
              disabled={disabled}
              onChange={(event) => setCountryInput(event.target.value.toUpperCase())}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleAddCountry()
                }
              }}
            />
            <Button
              variant='contained'
              onClick={handleAddCountry}
              disabled={disabled || !countryInput.trim()}
            >
              Add
            </Button>
          </Box>
          <Box display='flex' flexWrap='wrap' gap={1}>
            {countries.length > 0 ? (
              countries.map((country) => (
                <Chip
                  key={country}
                  label={country}
                  color='primary'
                  variant='outlined'
                  onDelete={disabled ? undefined : () => handleRemoveCountry(country)}
                />
              ))
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No countries added.
              </Typography>
            )}
          </Box>
        </Box>
      )
    }

    const isObjectLike = typeof value === 'object' && value !== null
    const textValue = isObjectLike
      ? JSON.stringify(value, null, 2)
      : value === undefined || value === null
      ? ''
      : String(value)

    return (
      <TextField
        key={field}
        fullWidth
        size='small'
        label={toLabel(field)}
        value={textValue}
        disabled={disabled}
        multiline={isObjectLike || field === 'description'}
        minRows={isObjectLike || field === 'description' ? 3 : 1}
        onChange={(event) => {
          if (!disabled) {
            handleChangeField(field, parseInputValue(field, event.target.value, value))
          }
        }}
      />
    )
  }

  return (
    <>
      <IconButton
        size='small'
        sx={{
          p: 0.5,
          color: 'grey.600',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'grey.700'
          }
        }}
        onClick={(event) => {
          event.stopPropagation()
          setIsPricesExpanded(false)
          setTagInput('')
          setCountryInput('')
          setOpen(true)
        }}
      >
        <SettingsIcon fontSize='small' />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth='lg'
        aria-labelledby='edit-catalog-title'
      >
        <DialogTitle id='edit-catalog-title'>Edit Catalog</DialogTitle>
        <DialogContent dividers>
          {isLoading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {fieldsToRender.map((field) => (
                <Grid item xs={12} md={field === 'prices' ? 12 : 6} key={field}>
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button
            variant='contained'
            onClick={() => void handleSubmit()}
            disabled={isLoading || isSubmitting || !hasChanges}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
